/**
 * Encodes a string for safe use in JavaScript context
 * 
 * This prevents XSS when embedding user data in:
 * - JavaScript strings
 * - Script tags
 * - Event handlers
 * - JSON strings
 * 
 * @param value - The string to encode
 * @returns JavaScript-safe encoded string
 * 
 * @example
 * const userInput = "'; alert('XSS'); //";
 * const safe = encodeJavaScript(userInput);
 * // safe = "\\x27; alert(\\x27XSS\\x27); \\/\\/"
 * 
 * // Use in JavaScript context:
 * <script>var name = '${safe}';</script>
 */
export const encodeJavaScript = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  const str = String(value);

  return str
    .replace(/\\/g, '\\\\')  // Backslash
    .replace(/'/g, "\\'")     // Single quote
    .replace(/"/g, '\\"')     // Double quote
    .replace(/\n/g, '\\n')    // Newline
    .replace(/\r/g, '\\r')    // Carriage return
    .replace(/\t/g, '\\t')    // Tab
    .replace(/\f/g, '\\f')    // Form feed
    .replace(/\v/g, '\\v')    // Vertical tab
    .replace(/\0/g, '\\0')    // Null byte
    .replace(/</g, '\\x3C')   // Less than (prevents </script>)
    .replace(/>/g, '\\x3E')   // Greater than
    .replace(/&/g, '\\x26')   // Ampersand
    .replace(/=/g, '\\x3D')   // Equals
    .replace(/\+/g, '\\x2B')  // Plus
    .replace(/-/g, '\\x2D')   // Dash (prevents <!-- -->)
    .replace(/\//g, '\\/');   // Forward slash (prevents </script>)
};

/**
 * Encodes a value for safe use in JSON context
 * 
 * @param value - The value to encode
 * @returns JSON-safe string
 * 
 * @example
 * const userInput = { name: "<script>alert('XSS')</script>" };
 * const safe = encodeJson(userInput);
 * // safe = '{"name":"<script>alert(\'XSS\')</script>"}'
 */
export const encodeJson = (value: any): string => {
  return JSON.stringify(value);
};

/**
 * Encodes a string for safe use in inline event handlers
 * 
 * @param value - The string to encode
 * @returns Encoded string safe for event handlers
 * 
 * @example
 * const userInput = "alert('XSS')";
 * const safe = encodeEventHandler(userInput);
 * // Use: <div onclick="handleClick('${safe}')">
 */
export const encodeEventHandler = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  return encodeJavaScript(value)
    .replace(/\(/g, '\\x28')
    .replace(/\)/g, '\\x29')
    .replace(/;/g, '\\x3B');
};

